import { faMinus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useListZones } from 'src/features/zone/api/useListZones';
import { WorkingHoursFormSchemaValues } from 'src/features/workingHours/utils/formSchema';
import { DAY_OF_WEEK } from 'src/features/workingHours/utils/types';
import {
    DAY_OF_WEEK_TRANSLATIONS_MAP,
} from 'src/features/workingHours/utils/constants';
import {
    BOOLEAN_MAP,
    BOOLEAN_OPTIONS,
} from 'src/features/sites/utils/constants';
import { workingHoursMessages } from 'src/features/workingHours/utils/messages';
import { Form, FormControl, FormControlStack, FormItem, FormLabel, FormMessage, FormField } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from 'src/registry/ui/Select';
import { useParking } from 'src/providers/ParkingProvider';
import SelectFormField from 'src/registry/components/forms/SelectFormField';

type WorkingHoursFormProps = {
    form: UseFormReturn<WorkingHoursFormSchemaValues>;
    onSubmit: (values: WorkingHoursFormSchemaValues) => void;
};

function WorkingHoursForm({ onSubmit, form }: WorkingHoursFormProps) {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();

    const { data: zonesData } = useListZones({
        parkingId,
    });

    const { all_day } = form.watch();

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <FormField
                control={form.control}
                name="zone"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage
                                id={workingHoursMessages.zone.id}
                                defaultMessage={workingHoursMessages.zone.defaultMessage}
                            />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select onValueChange={field.onChange} value={field.value}>
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder={`${formatMessage(workingHoursMessages.zone)}...`} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {zonesData?.results.map((option) => (
                                            <SelectItem key={option.id} value={option.id}>
                                                {
                                                    option.translations.find(
                                                        (translation) => translation.language === locale,
                                                    )?.name
                                                }
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <SelectFormField<WorkingHoursFormSchemaValues>
                form={form}
                required
                name="day_of_week"
                label={formatMessage(workingHoursMessages.weekday)}
                options={Object.values(DAY_OF_WEEK)}
                optionTranslationMap={DAY_OF_WEEK_TRANSLATIONS_MAP}
            />
            <SelectFormField<WorkingHoursFormSchemaValues>
                form={form}
                required
                name="all_day"
                isBoolean
                label={formatMessage(workingHoursMessages.allDay)}
                options={BOOLEAN_OPTIONS}
                optionTranslationMap={BOOLEAN_MAP}
                onChange={(value) => {
                    const isAllDay = JSON.parse(value) as boolean;
                    form.setValue('start_time', isAllDay ? null : '00:00:00');
                    form.setValue('end_time', isAllDay ? null : '23:59:59');
                }}
            />
            <FormField
                control={form.control}
                name="start_time"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">{formatMessage(workingHoursMessages.timeFrame)}</FormLabel>
                        <div className="flex w-full items-center gap-4">
                            <FormControlStack>
                                <FormControl>
                                    <Input
                                        disabled={all_day}
                                        type="time"
                                        className="w-full"
                                        step={1}
                                        {...field}
                                        value={field.value || '00:00:00'}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                            <FontAwesomeIcon className="h-10 w-3 text-sm text-gray-400" icon={faMinus} />
                            <FormField
                                control={form.control}
                                name="end_time"
                                render={({ field }) => (
                                    <FormControlStack>
                                        <FormControl>
                                            <Input
                                                disabled={all_day}
                                                type="time"
                                                className="w-full"
                                                step={1}
                                                {...field}
                                                value={field.value || '23:59:59'}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormControlStack>
                                )}
                            />
                        </div>
                    </FormItem>
                )}
            />
        </Form>
    );
}

export default WorkingHoursForm;
